/**
 * Opening a message in its own window (ADR 0020).
 *
 * A real `window.open` onto a real route, not a modal: the owner asked for
 * something they can drag to the other monitor and close with the OS. Because
 * the route is same-origin, the `__Host-` cookie trio and the CSRF cookie come
 * with it and there is nothing to authenticate; because it is a route and not a
 * popup-only document, it survives a reload and works when opened directly with
 * no `opener` at all.
 *
 * `noopener` is deliberately NOT passed. The window is our own document on our
 * own origin, and dropping the opener would make `window.open` return `null`,
 * so a second double-click could not focus the window that is already open.
 */

/** Per-viewer convenience only, so every read and write is guarded. */
const SIZE_KEY = 'ols.msgwin.size';

export const MESSAGE_WINDOW_DEFAULT_WIDTH = 880;
export const MESSAGE_WINDOW_DEFAULT_HEIGHT = 820;

/** The route a message window renders. Also a perfectly ordinary bookmarkable URL. */
export function messageWindowPath(accountId: string, messageId: string): string {
  return `/message/${encodeURIComponent(accountId)}/${encodeURIComponent(messageId)}`;
}

/**
 * One window per message: a repeat double-click on the same row targets the
 * same window name, so the browser focuses the existing one instead of opening
 * a twin.
 */
export function messageWindowName(messageId: string): string {
  return `ols-msg-${messageId}`;
}

interface StoredSize {
  width: number;
  height: number;
}

function readStoredSize(): StoredSize | undefined {
  try {
    const raw = localStorage.getItem(SIZE_KEY);
    if (raw === null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return undefined;
    const { width, height } = parsed as Partial<StoredSize>;
    if (typeof width !== 'number' || typeof height !== 'number') return undefined;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width < 320 || height < 240) {
      return undefined;
    }
    return { width, height };
  } catch {
    return undefined;
  }
}

/** Remembers the size the user dragged the window to. Never throws. */
export function rememberMessageWindowSize(size: StoredSize): void {
  try {
    if (!Number.isFinite(size.width) || !Number.isFinite(size.height)) return;
    if (size.width < 320 || size.height < 240) return;
    localStorage.setItem(SIZE_KEY, JSON.stringify({ width: size.width, height: size.height }));
  } catch {
    /* private mode, blocked site data — the default size is fine */
  }
}

export interface OpenMessageWindowOptions {
  accountId: string;
  messageId: string;
}

export interface OpenMessageWindowResult {
  /** `false` when the browser or an extension blocked the window. */
  opened: boolean;
  url: string;
}

/**
 * Opens (or focuses) the message window.
 *
 * Returns `{opened: false}` when `window.open` answered `null` — the caller
 * falls back to the reading pane and says so in the live region, rather than
 * leaving the user with a double-click that silently did nothing.
 */
export function openMessageWindow({
  accountId,
  messageId,
}: OpenMessageWindowOptions): OpenMessageWindowResult {
  const url = messageWindowPath(accountId, messageId);

  const stored = readStoredSize();
  const availHeight = window.screen?.availHeight;
  const heightCap = typeof availHeight === 'number' && availHeight > 0 ? availHeight - 80 : undefined;
  const width = stored?.width ?? MESSAGE_WINDOW_DEFAULT_WIDTH;
  const wanted = stored?.height ?? MESSAGE_WINDOW_DEFAULT_HEIGHT;
  const height = heightCap === undefined ? wanted : Math.min(wanted, heightCap);

  // Centred over the opener rather than over the screen: on a multi-monitor
  // desk the window the user is looking at is the one it should land on.
  const left = Math.round((window.screenX ?? 0) + ((window.outerWidth || width) - width) / 2);
  const top = Math.round((window.screenY ?? 0) + ((window.outerHeight || height) - height) / 2);

  const features = [
    'popup=yes',
    `width=${Math.round(width)}`,
    `height=${Math.round(height)}`,
    `left=${left}`,
    `top=${top}`,
  ].join(',');

  const handle = window.open(url, messageWindowName(messageId), features);
  if (!handle) return { opened: false, url };
  try {
    handle.focus();
  } catch {
    /* a window we are allowed to open but not to focus is still open */
  }
  return { opened: true, url };
}
